import React, { useEffect, useState, useCallback } from 'react';
import { mutate as swrMutate } from 'swr';
import GenericModulePage from '../../components/generic/GenericModulePage';
import { initializeModule } from '../../utils/configInit';
import {
  reportScheduleConfig,
  REPORT_SCHEDULE_EVENTS,
} from './reportScheduleConfig';
import { createReportScheduleValidationSchema } from './reportScheduleValidation';
import ReportScheduleHistoryModal from './components/ReportScheduleHistoryModal';
import { AlertDialog } from '../../components/ui';
import { API_BASE_URL } from '../../config/apiConfig';

const { config: initializedConfig } = initializeModule(
  reportScheduleConfig,
  createReportScheduleValidationSchema,
);

const ReportSchedulePage = () => {
  const [historyTarget, setHistoryTarget] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [alert, setAlert] = useState({
    show: false,
    message: '',
    type: 'success',
  });

  const triggerRunNow = useCallback(async (target) => {
    if (!target?.id || runningId) return;
    setRunningId(target.id);
    try {
      const token = localStorage.getItem('authToken');
      const res = await fetch(
        `${API_BASE_URL}/report-schedules/${target.id}/run`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            ...(token ? { Authorization: `Bearer ${token}` } : {}),
          },
        },
      );
      const data = await res.json().catch(() => ({}));
      if (!res.ok) {
        throw new Error(data?.message || `Run failed (${res.status})`);
      }
      const status = data?.status || 'sent';
      const message =
        status === 'sent'
          ? `Report "${target.name || target.id}" sent${data?.error ? ` (notice: ${data.error})` : ''}.`
          : status === 'skipped'
            ? `Skipped: ${data?.error || 'no recipients / disabled'}.`
            : `Failed: ${data?.error || 'unknown error'}`;
      setAlert({
        show: true,
        message,
        type:
          status === 'sent' ? 'success' : status === 'failed' ? 'error' : 'warning',
      });
      // Refresh list so updated lastRunAt/lastStatus reflect immediately.
      try {
        await swrMutate(initializedConfig.api.baseEndpoint);
      } catch {
        /* ignore */
      }
    } catch (err) {
      setAlert({
        show: true,
        message: err?.message || 'Run failed',
        type: 'error',
      });
    } finally {
      setRunningId(null);
    }
  }, [runningId]);

  useEffect(() => {
    const onRunNow = (event) => {
      const target = event?.detail || {};
      if (target?.id) triggerRunNow(target);
    };
    const onHistory = (event) => {
      const target = event?.detail || {};
      if (target?.id) setHistoryTarget(target);
    };
    window.addEventListener(REPORT_SCHEDULE_EVENTS.RUN_NOW, onRunNow);
    window.addEventListener(REPORT_SCHEDULE_EVENTS.HISTORY, onHistory);
    return () => {
      window.removeEventListener(REPORT_SCHEDULE_EVENTS.RUN_NOW, onRunNow);
      window.removeEventListener(REPORT_SCHEDULE_EVENTS.HISTORY, onHistory);
    };
  }, [triggerRunNow]);

  return (
    <>
      <GenericModulePage config={initializedConfig} />
      <ReportScheduleHistoryModal
        isOpen={!!historyTarget}
        onClose={() => setHistoryTarget(null)}
        scheduleId={historyTarget?.id}
        scheduleName={historyTarget?.name}
      />
      <AlertDialog
        isOpen={alert.show}
        onClose={() => setAlert({ show: false, message: '', type: 'success' })}
        title=""
        message={alert.message}
        type={alert.type}
        confirmText="OK"
      />
    </>
  );
};

ReportSchedulePage.displayName = 'ReportSchedulePage';
ReportSchedulePage.config = initializedConfig;

export default ReportSchedulePage;
